// ContributedProjects.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ProjectPlaceholder from "../images/CinevestorLogo.png";
import "../CSS/ContributedProjects.css";
import { BASE_API_URL } from "../constants";
import html2canvas from "html2canvas";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
const API_BASE_URL = `${BASE_API_URL}/api`;

// Extracts YouTube video ID from any YouTube URL format
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

const safeToString = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
};

// Renders a badge icon no matter what shape the backend sends:
// - Font Awesome class string ("fas fa-trophy", "fa-trophy") -> <i>
// - full image URL -> <img>
// - object with { src } or { url } -> <img>
// - emoji / plain text -> shown as-is
// - anything missing/unrecognized -> falls back to a visible medal icon
//   instead of rendering blank
const RenderBadgeIcon = ({ icon }) => {
  if (React.isValidElement(icon)) return icon;

  if (typeof icon === "string") {
    const trimmed = icon.trim();

    if (!trimmed) {
      return <i className="fas fa-medal" aria-hidden="true"></i>;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return (
        <img
          src={trimmed}
          alt=""
          onError={(e) => {
            if (e?.target) e.target.style.display = "none";
          }}
          style={{ width: 22, height: 22, objectFit: "cover", borderRadius: 6 }}
        />
      );
    }

    // Font Awesome class string, e.g. "fas fa-trophy" or just "fa-trophy"
    if (/(^|\s)fa-[\w-]+/.test(trimmed)) {
      const hasStylePrefix = /\bfa[srlb]?\b/.test(trimmed);
      const className = hasStylePrefix ? trimmed : `fas ${trimmed}`;
      return <i className={className} aria-hidden="true"></i>;
    }

    // short emoji or plain text label
    return <span aria-hidden="true">{trimmed}</span>;
  }

  if (icon && typeof icon === "object") {
    const src = icon.src || icon.url;
    if (typeof src === "string") {
      return (
        <img
          src={src}
          alt=""
          onError={(e) => {
            if (e?.target) e.target.style.display = "none";
          }}
          style={{ width: 22, height: 22, objectFit: "cover", borderRadius: 6 }}
        />
      );
    }
  }

  return <i className="fas fa-medal" aria-hidden="true"></i>;
};

// Small reusable collapsible section used for every card lower on the page.
// Keeps the header/icon/chevron markup in one place instead of repeating it.
const AccordionSection = ({ id, title, icon, isOpen, onToggle, count, children }) => (
  <div className={`cpv-acc-section ${isOpen ? "open" : ""}`}>
    <button
      type="button"
      className="cpv-acc-header"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`acc-panel-${id}`}
    >
      <span className="cpv-acc-header-left">
        <i className={`fas ${icon} cpv-acc-icon`} aria-hidden="true"></i>
        <span className="cpv-acc-title">{title}</span>
        {typeof count === "number" && count > 0 && (
          <span className="cpv-acc-count">{count}</span>
        )}
      </span>
      <i className="fas fa-chevron-down cpv-acc-chevron" aria-hidden="true"></i>
    </button>
    <div className="cpv-acc-body-wrap" id={`acc-panel-${id}`}>
      <div className="cpv-acc-body">{children}</div>
    </div>
  </div>
);

const ContributedProjects = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [profitData, setProfitData] = useState([]);
  const [projectUpdates, setProjectUpdates] = useState([]);
  const [imgFailed, setImgFailed] = useState(false);
  const [fullName, setFullName] = useState(sessionStorage.getItem("userFullName") || sessionStorage.getItem("userName") || "Contributor");
  const [posterDataUrl, setPosterDataUrl] = useState(null);
  const [referralCode, setReferralCode] = useState(sessionStorage.getItem("referralCode") || "");

  const [openSections, setOpenSections] = useState({
    updates: true,
    returns: true,
    badges: true,
    about: true,
    metrics: false,
    funding: false,
  });

  const bannerRef = useRef(null);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Fetch an external image URL as a base64 data URL using multi-strategy (CORS then Proxy)
  const fetchImageAsDataUrl = async (imageUrl) => {
    const fetchBlob = async (url) => {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('Fetch failed');
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    try {
      // Strategy 1: Direct CORS fetch
      return await fetchBlob(imageUrl);
    } catch (err) {
      console.warn('CORS fetch failed, trying proxy...', err);
      try {
        // Strategy 2: images.weserv.nl proxy
        const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
        return await fetchBlob(proxyUrl);
      } catch (proxyErr) {
        console.error('All fetch strategies failed for:', imageUrl);
        return null;
      }
    }
  };

  const generateBannerCanvas = async () => {
    if (!bannerRef.current) return null;
    // Before capturing, find all images inside the banner and swap src to data URLs if available
    const imgEls = bannerRef.current.querySelectorAll('img');
    const origSrcs = [];
    for (const img of imgEls) {
      origSrcs.push(img.src);
      // Try converting to data URL for CORS-free capture
      if (img.src && !img.src.startsWith('data:')) {
        const dataUrl = await fetchImageAsDataUrl(img.src);
        if (dataUrl) img.src = dataUrl;
      }
    }
    let canvas;
    try {
      canvas = await html2canvas(bannerRef.current, { scale: 2, useCORS: true, allowTaint: true, logging: false });
    } finally {
      // Restore original srcs
      imgEls.forEach((img, i) => { img.src = origSrcs[i]; });
    }
    return canvas;
  };

  const downloadImage = async () => {
    try {
      const canvas = await generateBannerCanvas();
      if (!canvas) return;
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "Cinevestor_Contributor_Badge.png";
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Failed to generate image", err);
    }
  };

  const copyCaption = () => {
    const title = project?.title ? safeToString(project.title) : "a project";
    const slug = title.trim().replace(/\s+/g, '-');
    const projectUrl = `https://cinevestor.in/project/${projectId}/${slug}${referralCode ? `?ref=${referralCode}` : ''}`;
    const text = `I just contributed to ${title} on Cinevestor! Join me in supporting great projects. Check it out here: ${projectUrl}`;
    navigator.clipboard.writeText(text)
      .then(() => alert("Caption copied to clipboard!"))
      .catch(err => console.error("Failed to copy caption", err));
  };

  const shareOnSocial = async (platform) => {
    const title = project?.title ? safeToString(project.title) : "a project";
    const slug = title.trim().replace(/\s+/g, '-');
    const projectUrl = `https://cinevestor.in/project/${projectId}/${slug}${referralCode ? `?ref=${referralCode}` : ''}`;
    const shareText = `I just contributed to ${title} on Cinevestor! Join me in supporting great projects. Check it out here: ${projectUrl}`;
    
    // MOBILE: Try native OS share sheet first (opens share picker with card image attached)
    if (navigator.canShare) {
      try {
        let imageFile = null;
        const canvas = await generateBannerCanvas();
        if (canvas) {
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          imageFile = new File([blob], "Cinevestor_Contributor_Badge.png", { type: "image/png" });
        }
        
        if (imageFile && navigator.canShare({ files: [imageFile] })) {
           await navigator.share({
             files: [imageFile],
             title: 'Cinevestor Contributor Badge',
             text: shareText
           });
           return; // Native share succeeded, done!
        }
      } catch (error) {
        if (error.name === 'AbortError') return; // User cancelled native share, do nothing
        // If native share fails, fall through to desktop URL sharing
      }
    }

    // DESKTOP: Directly open the platform, no download, no popup
    let shareUrl = '';
    switch(platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(projectUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'instagram':
        shareUrl = `https://www.instagram.com/cinevestor/`; 
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(projectUrl)}&summary=${encodeURIComponent(shareText)}`;
        break;
      default:
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const userId = sessionStorage.getItem("userId");
        if (!userId) {
          setError("User ID not found. Please log in again.");
          setLoading(false);
          return;
        }

        const resp = await axios.get(
          `${API_BASE_URL}/projects/${projectId}/user/${userId}`
        );

        const data = resp && resp.data && typeof resp.data === "object" ? resp.data : null;
        setProject(data);

        // ── Convert first poster to base64 data URL for CORS-free rendering in canvas ──
        try {
          if (data && Array.isArray(data.posters) && data.posters.length > 0) {
            const firstPoster = data.posters[0];
            let rawUrl = typeof firstPoster === "string"
              ? (firstPoster.startsWith("http") ? firstPoster : `${BASE_API_URL}${firstPoster}`)
              : (firstPoster?.url || firstPoster?.src || null);
            if (rawUrl) {
              const dataUrl = await fetchImageAsDataUrl(rawUrl);
              if (dataUrl) setPosterDataUrl(dataUrl);
            }
          }
        } catch (posterErr) {
          console.warn("Could not preload poster as data URL:", posterErr);
        }
        // ────────────────────────────────────────────────────────────────────────────

        const badgeResp = await axios.get(`${API_BASE_URL}/badges/user/${userId}/project/${projectId}`);
        const badgeData = badgeResp && Array.isArray(badgeResp.data) ? badgeResp.data : [];
        setBadges(badgeData);

        try {
          const profitResp = await axios.get(
            `${API_BASE_URL}/contributions/profit-summary?userId=${userId}`
          );
          const allContribs = profitResp?.data?.contributions || [];
          const filtered = allContribs.filter(
            (c) => String(c.projectId) === String(projectId)
          );
          setProfitData(filtered);
        } catch (profitErr) {
          console.warn("Profit summary not available:", profitErr);
          setProfitData([]);
        }

        // ── Fetch project updates timeline ──────────────────────────
        try {
          const updatesResp = await axios.get(
            `${API_BASE_URL}/projects/${projectId}/updates`
          );
          setProjectUpdates(updatesResp?.data?.updates || []);
        } catch (updErr) {
          console.warn("Project updates not available:", updErr);
          setProjectUpdates([]);
        }
        // ────────────────────────────────────────────────────────────
        
        // ── Fetch Profile for Full Name + Referral Code ────────────────────────
        try {
          const userEmail = sessionStorage.getItem("email");
          if (userEmail) {
            const profileRes = await axios.get(`${API_BASE_URL}/user-profile?email=${encodeURIComponent(userEmail)}`);
            const u = profileRes?.data?.user;
            if (u) {
              const name = `${u.firstname || ""} ${u.lastname || ""}`.trim();
              if (name) {
                setFullName(name);
                sessionStorage.setItem("userFullName", name);
              }
              if (u.referralCode) {
                setReferralCode(u.referralCode);
                sessionStorage.setItem("referralCode", u.referralCode);
              }
            }
          }
        } catch (profileErr) {
          console.warn("Could not fetch profile for full name", profileErr);
        }
        // ────────────────────────────────────────────────────────────
      } catch (err) {
        console.error("Error fetching project or badges:", err);
        setError("Failed to load project details.");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  if (loading) {
    return (
      <div className="cpv-page">
        <div className="cpv-ambient"><span className="a"></span><span className="b"></span></div>
        <div className="cpv-loading-wrap">
          <div className="cpv-spinner"></div>
          <p className="cpv-loading">Loading your project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpv-page">
        <div className="cpv-ambient"><span className="a"></span><span className="b"></span></div>
        <p className="cpv-error"><i className="fas fa-triangle-exclamation"></i> {safeToString(error)}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="cpv-page">
        <div className="cpv-ambient"><span className="a"></span><span className="b"></span></div>
        <p className="cpv-empty"><i className="fas fa-folder-open"></i> Project not found.</p>
      </div>
    );
  }

  // Normalize posters array: only keep strings, convert others to placeholder
  const posters =
    Array.isArray(project.posters) && project.posters.length > 0
      ? project.posters.map((p) => {
        if (typeof p === "string") {
          return p.startsWith("http") ? p : `${BASE_API_URL}${p}`;
        }
        if (p && typeof p === "object") {
          if (typeof p.url === "string") return p.url;
          if (typeof p.src === "string") return p.src;
        }
        return ProjectPlaceholder;
      })
      : [ProjectPlaceholder];

  const mainImage = posters[activeImageIndex] || ProjectPlaceholder;

  // Hero strip stats — pulled from the first profit record when available,
  // falling back to project-level fields so the header never renders blank.
  const heroProfit = profitData[0];
  const heroCategory = safeToString(project.category) || "Production";
  const heroLanguage = safeToString(project.language) || "N/A";
  const heroReturns = heroProfit
    ? `${heroProfit.assignedProfitPercent}%`
    : project.assignedProfitPercent
      ? `${project.assignedProfitPercent}%`
      : "N/A";
  const heroStatusRaw = heroProfit?.status || project.stage || "Active";
  const heroStatus = safeToString(heroStatusRaw);
  const heroStatusIsActive = /active/i.test(heroStatus);

  // Dynamically compute contributor tier based on total contribution amount
  const getContributorTier = (amount) => {
    const amt = Number(amount) || 0;
    if (amt >= 40000) return { label: 'Producers Circle', emoji: '🎥', color: '#a8d8ea' };
    if (amt >= 30000) return { label: 'Platinum Take',    emoji: '💎', color: '#a8d8ea' };
    if (amt >= 20000) return { label: 'Golden Frame',     emoji: '🏆', color: '#f5a623' };
    if (amt >= 10000) return { label: 'Silver Reel',      emoji: '🥈', color: '#94a3b8' };
    return                   { label: 'Spotlight Contributor',        emoji: '🎬', color: '#cd7f32' };
  };
  const contributorTier = getContributorTier(project.totalAmount);

  return (
    <div className="cpv-page">
      <div className="cpv-ambient">
        <span className="a"></span>
        <span className="b"></span>
      </div>

      <div className="cpv-layout-wrapper">
        {/* ══════════ BANNER SIDEBAR ══════════ */}
        <div className="cpv-banner-sidebar">
          {/* Visual wrapper that unifies the exportable card and the footer */}
          <div className="cpv-banner-visual-wrapper">
            
            {/* Card wrapped for html2canvas — Portrait 4:5 ratio for IG/FB */}
            <div className="cpv-banner-card-export" ref={bannerRef}>

              {/* ── TOP SECTION: Logo + Congrats + User Info ── */}
              <div className="cpv-banner-top">
                {/* Logo + Brand pill */}
                <div className="cpv-banner-logo-row">
                  <div className="cpv-banner-logo-pill">
                    <img
                      src={process.env.PUBLIC_URL + "/cinevestor_black_logo.jpeg"}
                      alt="Cinevestor"
                      className="cpv-banner-logo-img"
                    />
                  </div>
                  <span className="cpv-banner-tag">Official Contributor Badge</span>
                </div>

                <h2 className="cpv-banner-title">🎉 CONGRATULATIONS!</h2>

                {/* User Row */}
                <div className="cpv-banner-user-row">
                  <img
                    src={sessionStorage.getItem("userPhotoUrl") || "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"}
                    alt="User"
                    className="cpv-banner-small-avatar"
                    onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"; }}
                  />
                  <div className="cpv-banner-user-info">
                    <h3 className="cpv-banner-username">{fullName}</h3>
                    <div className="cpv-banner-usertag" style={{ color: contributorTier.color }}>
                      {contributorTier.emoji} {contributorTier.label}
                    </div>
                  </div>
                </div>

                <p className="cpv-banner-subtitle">You are an official contributor of Cinevestor movie</p>

                {/* Project Name Pill */}
                <div className="cpv-banner-project-pill">
                  🎬 {safeToString(project.title) || "Project"}
                </div>
              </div>

              {/* ── MIDDLE: Portrait Movie Poster ── */}
              <div className="cpv-banner-poster-section">
                <div className="cpv-banner-movie-poster-wrap">
                  <img
                    src={posterDataUrl || mainImage}
                    alt="Movie Poster"
                    className="cpv-banner-movie-poster"
                    onError={(e) => { if (posterDataUrl) { e.target.src = mainImage; } }}
                  />
                </div>
              </div>

              {/* ── BOTTOM: Explore Project Row with QR ── */}
              <div className="cpv-banner-explore-row">
                <div className="cpv-banner-explore-info">
                  <span className="cpv-banner-explore-icon">🚀</span>
                  <div className="cpv-banner-explore-text">
                    <span className="cpv-banner-explore-title">Explore the Project</span>
                    <span className="cpv-banner-explore-url">cinevestor.in</span>
                  </div>
                </div>
                <div className="cpv-banner-qr-wrap">
                  <QRCodeSVG
                    value={`https://cinevestor.in/project/${projectId}/${safeToString(project.title).trim().replace(/\s+/g, '-')}${referralCode ? `?ref=${referralCode}` : ''}`}
                    size={64}
                    bgColor="#ffffff"
                    fgColor="#0f1b47"
                    level="M"
                  />
                </div>
              </div>

            </div>
          
          {/* ── Footer: Share actions (outside the exportable card) ── */}
          <div className="cpv-banner-footer">
            <p className="share-text">SHARE YOUR ACHIEVEMENT</p>
            <div className="cpv-banner-actions">
              <button onClick={downloadImage} className="action-btn download-btn" title="Download Image"><i className="fas fa-download"></i> Download</button>
              <button onClick={copyCaption} className="action-btn copy-btn" title="Copy Caption"><i className="fas fa-copy"></i> Copy Link</button>
            </div>
            <div className="cpv-banner-socials">
              <button onClick={() => shareOnSocial('whatsapp')} className="social-btn whatsapp" title="Share on WhatsApp"><i className="fab fa-whatsapp"></i></button>
              <button onClick={() => shareOnSocial('instagram')} className="social-btn instagram" title="Share on Instagram"><i className="fab fa-instagram"></i></button>
              <button onClick={() => shareOnSocial('facebook')} className="social-btn facebook" title="Share on Facebook"><i className="fab fa-facebook-f"></i></button>
              <button onClick={() => shareOnSocial('linkedin')} className="social-btn linkedin" title="Share on LinkedIn"><i className="fab fa-linkedin-in"></i></button>
            </div>
          </div>
          
          </div> {/* End Visual Wrapper */}
        </div>

        <div className="cpv-frame">
          <div className="cpv-breadcrumb">
          <i className="fas fa-house" aria-hidden="true"></i>
          <span>My Contributions</span>
          <i className="fas fa-chevron-right cpv-breadcrumb-sep" aria-hidden="true"></i>
          <span className="cpv-breadcrumb-current">{safeToString(project.title) || "Project"}</span>
        </div>

        <button className="cpv-back-btn" onClick={() => navigate("/updates")}>
          <i className="fas fa-arrow-left"></i> Back
        </button>

        {/* ===== HERO: poster + overlaid title/stats ===== */}
        <div className="cpv-hero">
          <div className="cpv-hero-image">
            {!imgFailed ? (
              <img
                src={mainImage}
                alt={safeToString(project.title) || "Project poster"}
                onError={(e) => {
                  if (mainImage === ProjectPlaceholder) {
                    setImgFailed(true);
                  } else if (e?.target) {
                    e.target.src = ProjectPlaceholder;
                  }
                }}
                loading="lazy"
              />
            ) : (
              <div className="cpv-hero-fallback">
                <i className="fas fa-clapperboard" aria-hidden="true"></i>
              </div>
            )}
            <div className="cpv-hero-overlay">
              <span className="cpv-status-tag">
                <i className="fas fa-circle-check" aria-hidden="true"></i> Contributed Successfully
              </span>
              <h1 className="cpv-project-title">{safeToString(project.title) || "Untitled Project"}</h1>

              <div className="cpv-hero-meta-row">
                <div className="hero-meta-item">
                  <span className="hmk">Category</span>
                  <span className="hmv">{heroCategory}</span>
                </div>
                <div className="hero-meta-item">
                  <span className="hmk">Language</span>
                  <span className="hmv">{heroLanguage}</span>
                </div>
                <div className="hero-meta-item">
                  <span className="hmk">Returns</span>
                  <span className="hmv gold">{heroReturns}</span>
                </div>
                <div className="hero-meta-item">
                  <span className="hmk">Status</span>
                  <span className={`hmv ${heroStatusIsActive ? "green" : "violet"}`}>{heroStatus}</span>
                </div>
              </div>

              <div className="cpv-hero-goal">
                <span className="ghk"><i className="fas fa-wallet" aria-hidden="true"></i> Your Contribution</span>
                <span className="ghv">
                  ₹{project.totalAmount ? Number(project.totalAmount).toLocaleString("en-IN") : "0"}
                </span>
              </div>
            </div>
          </div>

          {posters.length > 1 && (
            <div className="cpv-thumb-strip">
              {posters.map((src, idx) => (
                <button
                  key={`thumb-${idx}`}
                  className={`cpv-thumb ${idx === activeImageIndex ? "active" : ""}`}
                  onClick={async () => {
                    setActiveImageIndex(idx);
                    // Also update the banner card's poster to match
                    setPosterDataUrl(null); // clear old one immediately
                    const dataUrl = await fetchImageAsDataUrl(src);
                    if (dataUrl) setPosterDataUrl(dataUrl);
                  }}
                  aria-label={`Show image ${idx + 1}`}
                >
                  <img
                    src={src}
                    alt={`thumb-${idx}`}
                    onError={(e) => {
                      if (e?.target) e.target.src = ProjectPlaceholder;
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ===== SUMMARY: contribution + payment status ===== */}
        <div className="cpv-summary-row">
          <div className="cpv-summary-card contribution">
            <div className="summary-icon"><i className="fas fa-coins" aria-hidden="true"></i></div>
            <div className="summary-body">
              <span className="summary-label">Total Contribution</span>
              <span className="summary-value">
                ₹{project.totalAmount ? Number(project.totalAmount).toLocaleString("en-IN") : "0"}
              </span>
            </div>
          </div>

          <div className="cpv-summary-card payment">
            <div className="summary-icon"><i className="fas fa-circle-check" aria-hidden="true"></i></div>
            <div className="summary-body">
              <span className="summary-label">Payment Status</span>
              <span className="summary-value">Payment Success</span>
            </div>
          </div>
        </div>

        {/* ===== ACCORDION SECTIONS ===== */}
        <div className="cpv-accordion">
          {profitData.length > 0 && (
            <AccordionSection
              id="returns"
              title="Your Return Summary"
              icon="fa-chart-line"
              isOpen={openSections.returns}
              onToggle={() => toggleSection("returns")}
              count={profitData.length}
            >
              <div className="cpv-profit-section">
                {profitData.map((c, idx) => {
                  const dayPct = (c.assignedProfitPercent / 548).toFixed(4);
                  const dayAmt = ((c.amount * parseFloat(dayPct)) / 100).toFixed(2);
                  const progress = Math.min(100, (c.daysElapsed / c.totalDays) * 100).toFixed(1);

                  return (
                    <div key={idx} className="cpv-profit-card">
                      <div className="cpv-profit-project-name">
                        <i className="fas fa-film" aria-hidden="true"></i>
                        {c.projectName || "—"}
                      </div>

                      <div className="cpv-profit-stats">
                        <div className="cpv-profit-stat assigned">
                          <span className="cpv-profit-stat-label">Assigned Returns</span>
                          <span className="cpv-profit-stat-value">{c.assignedProfitPercent}%</span>
                          <span className="cpv-profit-stat-sub">
                            of ₹{Number(c.amount).toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="cpv-profit-stat today">
                          <span className="cpv-profit-stat-label">Today&apos;s Accrual</span>
                          <span className="cpv-profit-stat-value">+{dayPct}%</span>
                          <span className="cpv-profit-stat-sub">₹{dayAmt} today</span>
                        </div>

                        <div className="cpv-profit-stat accrued">
                          <span className="cpv-profit-stat-label">Accrued Till Date</span>
                          <span className="cpv-profit-stat-value">
                            {c.cumulativeProfitPercent.toFixed(3)}%
                          </span>
                          <span className="cpv-profit-stat-sub">
                            ₹{Number(c.cumulativeProfitAmount).toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>

                      <div className="cpv-profit-progress-wrap">
                        <div className="cpv-profit-progress-meta">
                          <span>18-Month Journey</span>
                          <span>{c.daysElapsed} / {c.totalDays} days</span>
                        </div>
                        <div className="cpv-profit-bar">
                          <div className="cpv-profit-bar-fill" style={{ width: `${progress}%` }} />
                        </div>
                        <div className="cpv-profit-progress-pct">{progress}% complete</div>
                      </div>

                      <div className="cpv-profit-card-footer">
                        <span className={`cpv-profit-status-badge ${c.status}`}>
                          {c.status === "active" ? <><i className="fas fa-circle" /> Active</> : <><i className="fas fa-circle-check" /> Completed</>}
                        </span>
                        <span className="cpv-profit-total-label">
                          Total Contribution: ₹{Number(c.amount).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionSection>
          )}

          {Array.isArray(badges) && badges.length > 0 && (
            <AccordionSection
              id="badges"
              title="Your Contribution Badge"
              icon="fa-trophy"
              isOpen={openSections.badges}
              onToggle={() => toggleSection("badges")}
              count={badges.length}
            >
              {badges.map((b, idx) => {
                const tierName = b && (b.TierName || b.tierName || b.tier || "");
                const profit = b && (b.Profit || b.profit || "");
                const assignedRaw = b && (b.AssignedAt || b.assignedAt || b.assigned);
                let formattedDate = "N/A";
                try {
                  if (assignedRaw) {
                    const assignedStr = typeof assignedRaw === "string" ? assignedRaw.split(".")[0] : assignedRaw;
                    const d = new Date(assignedStr);
                    if (!Number.isNaN(d.getTime())) {
                      formattedDate = d.toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    }
                  }
                } catch {
                  formattedDate = "N/A";
                }

                return (
                  <div key={`badge-${idx}`} className="badge-item">
                    <div className="badge-icon">
                      <RenderBadgeIcon icon={b && (b.TierIcon || b.tierIcon || b.icon)} />
                    </div>
                    <div className="badge-body">
                      <div className="badge-title">{safeToString(tierName) || "Investor"}</div>
                      <div className="badge-meta">Potential Profit: {safeToString(profit) || "N/A"}</div>
                      <div className="badge-date">{formattedDate}</div>
                    </div>
                  </div>
                );
              })}
            </AccordionSection>
          )}

          <AccordionSection
            id="about"
            title="About This Project"
            icon="fa-circle-info"
            isOpen={openSections.about}
            onToggle={() => toggleSection("about")}
          >
            <p className="about-text">{safeToString(project.aboutProject) || "No description provided."}</p>
          </AccordionSection>

          <AccordionSection
            id="metrics"
            title="Project Metrics"
            icon="fa-chart-simple"
            isOpen={openSections.metrics}
            onToggle={() => toggleSection("metrics")}
          >
            <div className="cpv-meta">
              <div>
                <div className="mk">Project ID</div>
                <div className="mv">{safeToString(project.ProjectID) || "N/A"}</div>
              </div>
              <div>
                <div className="mk">Stage</div>
                <div className="mv">{safeToString(project.stage) || "N/A"}</div>
              </div>
              <div>
                <div className="mk">Category</div>
                <div className="mv">{safeToString(project.category || "Film / Media")}</div>
              </div>
              <div>
                <div className="mk">Language</div>
                <div className="mv">{safeToString(project.language) || "N/A"}</div>
              </div>
              <div>
                <div className="mk">Producer</div>
                <div className="mv">{safeToString(project.producer) || "N/A"}</div>
              </div>
              <div>
                <div className="mk">Experience</div>
                <div className="mv">{safeToString(project.experience) || "N/A"}</div>
              </div>
              <div>
                <div className="mk">Funding Goal</div>
                <div className="mv">
                  {project.fundingGoal ? Number(project.fundingGoal).toLocaleString() : "N/A"}
                </div>
              </div>
              <div>
                <div className="mk">Min Contribution</div>
                <div className="mv">
                  {project.minContribution ? Number(project.minContribution).toLocaleString() : "N/A"}
                </div>
              </div>
              <div>
                <div className="mk">Progress</div>
                <div className="mv">{safeToString(project.progressText) || "N/A"}</div>
              </div>
            </div>
          </AccordionSection>

          <AccordionSection
            id="funding"
            title="Funding Plan"
            icon="fa-sack-dollar"
            isOpen={openSections.funding}
            onToggle={() => toggleSection("funding")}
          >
            <div className="cpv-plan-row">
              <span className="k">Utilization</span>
              <span className="v">{safeToString(project.utilization || "Not specified")}</span>
            </div>
            <div className="cpv-plan-row">
              <span className="k">Recovery Plan</span>
              <span className="v">{safeToString(project.recoveryPlan || "Not specified")}</span>
            </div>
            <div className="cpv-plan-row">
              <span className="k">Profit Plan</span>
              <span className="v">{safeToString(project.profitPlan || "Not specified")}</span>
            </div>
          </AccordionSection>

          {/* ══════════ PROJECT UPDATES TIMELINE (MOVED TO SIDEBAR) ══════════ */}

        </div>

        <div className="cpv-divider-icon">
          <i className="fas fa-star" aria-hidden="true"></i>
        </div>

        <div className="cpv-footer-links">
          <Link to="/project" className="cpv-back-link">
            <i className="fas fa-arrow-left" aria-hidden="true"></i> Back to My Contributions
          </Link>
        </div>
      </div>

      {/* ══════════ PROJECT UPDATES SIDEBAR ══════════ */}
      <div className="cpv-updates-sidebar">
        <div className="cpv-sidebar-header">
          <i className="fas fa-bullhorn cpv-acc-icon" aria-hidden="true"></i>
          <span className="cpv-sidebar-title">Project Updates</span>
          {projectUpdates.length > 0 && (
            <span className="cpv-sidebar-badge">{projectUpdates.length}</span>
          )}
        </div>
        
        <div className="cpv-sidebar-body">
          {projectUpdates.length === 0 ? (
            <p className="cpv-updates-empty">
              <i className="fas fa-satellite-dish" aria-hidden="true"></i>&nbsp;
              No updates yet. Check back soon!
            </p>
          ) : (
            <div className="cpv-timeline">
              {projectUpdates.map((upd, idx) => {
                let dt;
                try {
                  dt = new Date(upd.createdAt);
                } catch {
                  dt = new Date();
                }
                const isValidDate = dt instanceof Date && !isNaN(dt.getTime());

                const timeStr = isValidDate ? dt.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }) : "00:00";

                const dateStr = isValidDate ? dt.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }) : "N/A";

                const ytId = getYouTubeId(upd.videoUrl);
                const isFirst = idx === 0;

                return (
                  <div
                    key={upd.id}
                    className={`cpv-tl-entry ${isFirst ? "first" : ""}`}
                  >
                    {/* ── Left: timestamp column ── */}
                    <div className="cpv-tl-time-col">
                      <span className="cpv-tl-time">{timeStr} IST</span>
                      <span className="cpv-tl-date">{dateStr}</span>
                    </div>

                    {/* ── Center: dot + spine line ── */}
                    <div className="cpv-tl-spine" aria-hidden="true">
                      <span className={`cpv-tl-dot ${isFirst ? "first" : ""}`}></span>
                      {idx < projectUpdates.length - 1 && (
                        <span className="cpv-tl-line"></span>
                      )}
                    </div>

                    {/* ── Right: content card ── */}
                    <div className="cpv-tl-card">
                      {isFirst && (
                        <span className="cpv-tl-new-badge">NEW</span>
                      )}

                      <h4 className="cpv-tl-title">{safeToString(upd.title)}</h4>

                      {upd.body && (
                        <p className="cpv-tl-body">{safeToString(upd.body)}</p>
                      )}

                      {upd.imageUrl && (
                        <div className="cpv-tl-media">
                          <img
                            src={upd.imageUrl}
                            alt="Project update"
                            className="cpv-tl-img"
                            loading="lazy"
                            onError={(e) => {
                              if (e?.target) e.target.style.display = "none";
                            }}
                          />
                        </div>
                      )}

                      {ytId && (
                        <div className="cpv-tl-media cpv-tl-video-wrap">
                          <iframe
                            src={`https://www.youtube.com/embed/${ytId}`}
                            className="cpv-tl-yt"
                            title="Project video update"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}

                      {upd.videoUrl && !ytId && (
                        <a
                          href={upd.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cpv-tl-link-btn"
                        >
                          <i className="fas fa-play-circle" aria-hidden="true"></i> Watch Video
                        </a>
                      )}

                      {upd.linkUrl && (
                        <a
                          href={upd.linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cpv-tl-link-btn"
                        >
                          <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>{" "}
                          {safeToString(upd.linkLabel) || "View More"}
                        </a>
                      )}

                      {(upd.videoUrl || upd.linkUrl) && (
                        <div className="cpv-tl-tags">
                          {upd.videoUrl && (
                            <span className="cpv-tl-tag vid">🎬 Video</span>
                          )}
                          {upd.linkUrl && (
                            <span className="cpv-tl-tag lnk">🔗 Link</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* ══════════ END PROJECT UPDATES SIDEBAR ══════════ */}
      </div>
    </div>
  );
};

export default ContributedProjects;